import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWHEPStream } from "@/lib/cctv";
import { cctvPath } from "@/routes/paths";
import { ChevronLeft, ChevronRight } from "@safers/ui/atoms";
import { GridLayout } from "@safers/ui/organisms";
import {
  Widget,
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from "@safers/ui/molecules";
import type { GridTemplate, LayoutChangeEvent } from "@safers/ui";
import type { CCTVStreamItem } from "@/hooks/useCCTVStreams";
import { StreamLoadingOverlay, StreamErrorOverlay, StreamStatusBadge } from "@/components/cctv";
import {
  useIsFullscreen,
  useLandscapeInFullscreen,
  useLandscapeVideo,
} from "@/hooks/useIsFullscreen";
import { useContainerSize } from "@/hooks/useContainerSize";
import { useRemPx } from "@/hooks/useRemPx";
import { PTZControl } from "./PTZControl";
import { PTZControlCompact } from "./PTZControlCompact";
import { isPlaybackEnabledSite, type StompEventResponse } from "@/services";

function CCTVSelect({
  items,
  selected,
  onChange,
}: {
  items: CCTVStreamItem[];
  selected: string;
  onChange: (streamName: string) => void;
}) {
  return (
    <Select value={selected} onValueChange={onChange}>
      <SelectTrigger className="w-auto min-w-[12rem] max-w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.streamName} value={item.streamName}>
            {item.displayName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type GridMode = "1x1" | "2x2" | "3x3" | "4x4" | "spot";

const GRID_MODE_LABELS: Record<GridMode, string> = {
  "1x1": "1×1",
  "2x2": "2×2",
  "3x3": "3×3",
  "4x4": "4×4",
  spot: "1+7",
};

const GRID_MODES: GridMode[] = ["1x1", "2x2", "3x3", "4x4", "spot"];

/**
 * 큰 PTZ 컨트롤러를 올릴 수 있는 최소 영상 크기.
 *
 * **가로세로를 함께 본다.** 2×2 타일은 폭은 넉넉해도 높이가 모자란다 — 폭만 보면
 * 큰 패널이 떠서 타일 밖으로 넘친다.
 *
 * 큰 패널은 `17.5rem` 폭이고 `bottom-16 right-16`(각 `4rem`) 안쪽에 `bottom-[4.5rem]`
 * 로 뜬다. 세로로 헤더·조이스틱·속도·프리셋·입력까지 쌓여 `8.5rem` 여백 위로
 * 스무 몇 rem 을 더 먹는다. 그래서 높이 쪽이 실제 제약이다.
 *
 * **px 가 아니라 rem 인 이유** — 루트 폰트가 해상도를 따라 커지므로
 * (FHD 100% · QHD 131.25% · 4K 200%) 관제 PC 에서는 같은 타일도 더 넉넉해진다.
 * px 로 박으면 4K 에서 실제보다 좁다고 판단한다.
 */
const WIDE_TILE_MIN_W_REM = 26;
const WIDE_TILE_MIN_H_REM = 34;
/** 임계값 부근에서 두 변형이 번갈아 뜨지 않도록 하는 여유 */
const WIDE_TILE_HYSTERESIS_REM = 2;

/** 패널이 먹는 세로 여백 — 컨테이너 바닥 인셋 + 패널이 FAB 위로 뜨는 거리 */
const PANEL_RESERVED_REM = { wide: 8.5, compact: 5.75 };

function createSquareTemplate(id: string, name: string, cols: number): GridTemplate {
  const total = cols * cols;
  return {
    id,
    name,
    columns: cols,
    rows: cols,
    cells: Array.from({ length: total }, (_, i) => ({
      id: `c${i + 1}`,
      colStart: (i % cols) + 1,
      colSpan: 1,
      rowStart: Math.floor(i / cols) + 1,
      rowSpan: 1,
    })),
  };
}

const GRID_TEMPLATES: Record<"2x2" | "3x3" | "4x4", GridTemplate> = {
  "2x2": createSquareTemplate("2x2", "2×2", 2),
  "3x3": createSquareTemplate("3x3", "3×3", 3),
  "4x4": createSquareTemplate("4x4", "4×4", 4),
};

const SPOTLIGHT_TEMPLATE: GridTemplate = {
  id: "spot",
  name: "1+7",
  columns: 4,
  rows: 4,
  cells: [
    { id: "big", colStart: 1, colSpan: 3, rowStart: 1, rowSpan: 3 },
    { id: "s1", colStart: 4, colSpan: 1, rowStart: 1, rowSpan: 1 },
    { id: "s2", colStart: 4, colSpan: 1, rowStart: 2, rowSpan: 1 },
    { id: "s3", colStart: 4, colSpan: 1, rowStart: 3, rowSpan: 1 },
    { id: "s4", colStart: 1, colSpan: 1, rowStart: 4, rowSpan: 1 },
    { id: "s5", colStart: 2, colSpan: 1, rowStart: 4, rowSpan: 1 },
    { id: "s6", colStart: 3, colSpan: 1, rowStart: 4, rowSpan: 1 },
    { id: "s7", colStart: 4, colSpan: 1, rowStart: 4, rowSpan: 1 },
  ],
};

function GridModeSelector({
  mode,
  onChange,
}: {
  mode: GridMode;
  onChange: (mode: GridMode) => void;
}) {
  return (
    <div className="flex gap-1 rounded-md border border-neutral-200 dark:border-[#2A2D3A] bg-white dark:bg-[#1A1D27] p-0.5">
      {GRID_MODES.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
            mode === m
              ? "bg-brand text-white"
              : "text-neutral-500 dark:text-white/40 hover:text-neutral-600 dark:hover:text-white/70"
          }`}
        >
          {GRID_MODE_LABELS[m]}
        </button>
      ))}
    </div>
  );
}

function LiveStreamPlayer({
  streamUrl,
  streamUrlHigh,
  name,
  compact,
  landscapeOnFullscreen,
  ptzCctvId,
  onFocus,
}: {
  streamUrl: string;
  streamUrlHigh?: string | null;
  name: string;
  compact?: boolean;
  landscapeOnFullscreen?: boolean;
  /** PTZ 가능한 카메라의 id. 없으면 PTZ 카메라가 아니다 */
  ptzCctvId?: number | null;
  /** 배지를 눌렀을 때. 그리드에서만 주고, 1×1 에서는 주지 않는다 */
  onFocus?: () => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const isFullscreen = useIsFullscreen(boxRef);
  useLandscapeInFullscreen(isFullscreen, landscapeOnFullscreen);

  const activeUrl = isFullscreen && streamUrlHigh ? streamUrlHigh : streamUrl;
  const { videoRef, status, error, connect } = useWHEPStream(activeUrl);

  // 고화질로 나가는 타일에서는 조작판을 띄운다. 저화질 타일(3×3·4×4)은 배지의 `PTZ`
  // 표시로만 알리고, 누르면 1×1 로 넘어간다.
  const showPtzControl = !compact && ptzCctvId != null;

  // 어느 컨트롤러를 띄울지는 **영상 영역을 실측해서** 정한다. 그리드 모드로 가르지
  // 않는 이유 — `compact` 가 화질을 뜻하는데 크기 대용으로 쓰다 어긋난 것이 이 코드의
  // 원래 문제였다. 실측이면 창 크기·모바일 가로세로·앞으로 생길 레이아웃까지 따라간다.
  const { width, height } = useContainerSize(boxRef);
  const pxPerRem = useRemPx();
  //
  // 임계값에 히스테리시스를 둔다. 창을 천천히 줄일 때 두 변형이 번갈아 뜨면 그때마다
  // 패널이 다시 마운트되어 프리셋을 새로 불러온다.
  //
  // effect 가 아니라 **렌더 중 파생**이다 — 폭이 바뀐 그 렌더에서 바로 맞는 컨트롤러가
  // 나온다. effect 로 하면 한 프레임 동안 이전 것이 비쳤다가 바뀐다.
  const [wideTile, setWideTile] = useState(false);
  const slack = wideTile ? WIDE_TILE_HYSTERESIS_REM : 0;
  const nextWideTile =
    width && height
      ? width / pxPerRem >= WIDE_TILE_MIN_W_REM - slack &&
        height / pxPerRem >= WIDE_TILE_MIN_H_REM - slack
      : wideTile;
  if (nextWideTile !== wideTile) setWideTile(nextWideTile);

  // 임계값을 넘겨도 패널이 타일보다 길어질 수 있다(짧고 넓은 창 등). 남는 높이를
  // 그대로 넘겨 패널이 스스로 잘라 쓰게 한다 — 넘쳐 나가는 것보다 안에서 스크롤이 낫다.
  const panelMaxHeight = height
    ? Math.max(0, height - PANEL_RESERVED_REM[wideTile ? "wide" : "compact"] * pxPerRem)
    : undefined;

  return (
    // `overflow-hidden` 을 걸지 않는다 — PTZ 패널이 타일 위로 열리는데 잘려 나간다.
    // 모서리는 video 자체에 둥글게 준다.
    <div ref={boxRef} className="relative h-full w-full rounded-lg bg-black">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        controls
        className="h-full w-full rounded-lg object-contain"
      />

      {status === "connecting" && <StreamLoadingOverlay compact={compact} />}
      {status === "failed" && (
        <StreamErrorOverlay compact={compact} message={error} onReconnect={() => connect()} />
      )}

      <StreamStatusBadge
        label="LIVE"
        name={name}
        status={status}
        compact={compact}
        ptz={!!ptzCctvId && compact}
        onClick={onFocus}
      />

      {/* 우·하 여백을 같게 둔다. 아래를 더 내리지 못하는 건 네이티브 video 컨트롤
          바가 그 자리를 쓰기 때문이다 — FAB 에 마우스를 가져가면 뜬다. */}
      {showPtzControl && (
        <div className={`absolute z-20 ${wideTile ? "bottom-16 right-16" : "bottom-8 right-8"}`}>
          {wideTile ? (
            <PTZControl key={ptzCctvId} cctvId={ptzCctvId} maxHeight={panelMaxHeight} />
          ) : (
            <PTZControlCompact key={ptzCctvId} cctvId={ptzCctvId} maxHeight={panelMaxHeight} />
          )}
        </div>
      )}
    </div>
  );
}

function EventVideoPlayer({
  event,
  onClose,
  landscapeOnFullscreen,
}: {
  event: StompEventResponse;
  onClose: () => void;
  landscapeOnFullscreen?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  useLandscapeVideo(boxRef, landscapeOnFullscreen);
  const hasVideo = !!event.video?.url;

  useEffect(() => {
    if (hasVideo && videoRef.current) {
      videoRef.current.load();
    }
  }, [event.video?.url, hasVideo]);

  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-black">
      {hasVideo ? (
        <video
          ref={videoRef}
          src={event.video!.url}
          controls
          autoPlay
          playsInline
          className="h-full w-full object-contain"
        />
      ) : (
        <div className="relative h-full w-full">
          {event.snapshot?.url && (
            <img
              src={event.snapshot.url}
              alt="snapshot"
              className="h-full w-full object-contain opacity-40"
            />
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="mb-3 h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            <p className="text-sm font-medium text-white">현장 영상 제작 중...</p>
            <p className="mt-1 text-xs text-white/40">완료되면 자동으로 재생됩니다</p>
          </div>
        </div>
      )}

      <div className="absolute left-0 right-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded bg-severity-danger-600/80 px-1.5 py-0.5 font-bold text-white">
            {event.type.replace("_", " ")}
          </span>
          <span className="text-white/60">{event.name}</span>
          <span className="text-white/40">{new Date(event.timestamp).toLocaleString("ko-KR")}</span>
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="absolute bottom-2 left-2 flex items-center gap-2 rounded-lg bg-black/60 px-2.5 py-1.5 text-xs font-bold backdrop-blur-sm">
        <span className={hasVideo ? "text-green-400" : "text-yellow-400"}>
          {hasVideo ? "REC" : "PROCESSING"}
        </span>
        <span className="h-3 w-px bg-white/40" />
        <span className="text-white">{event.name}</span>
      </div>
    </div>
  );
}

function EmptySlot() {
  return (
    <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-neutral-200 dark:border-[#2A2D3A] bg-white dark:bg-[#1A1D27]">
      <span className="text-xs text-neutral-400 dark:text-white/20">빈 채널</span>
    </div>
  );
}

function LiveGrid({
  items,
  gridMode,
  page,
  onFocusStream,
}: {
  items: CCTVStreamItem[];
  gridMode: "2x2" | "3x3" | "4x4";
  page: number;
  onFocusStream: (streamName: string) => void;
}) {
  const template = GRID_TEMPLATES[gridMode];
  const perPage = template.cells.length;
  const pageItems = items.slice(page * perPage, (page + 1) * perPage);
  const emptyCount = Math.max(0, perPage - pageItems.length);
  const compact = gridMode === "4x4" || gridMode === "3x3";

  return (
    <GridLayout template={template} gap={6} editable className="h-full overflow-y-visible p-0">
      {pageItems.map((item, i) => (
        <Widget
          key={item.streamName}
          id={`cam-${i}`}
          border={false}
          className="h-full rounded-lg bg-transparent shadow-none"
          contentClassName="h-full p-0"
        >
          <LiveStreamPlayer
            streamUrl={compact ? (item.whepUrlLow ?? item.whepUrl) : item.whepUrl}
            streamUrlHigh={item.whepUrl}
            name={item.displayName}
            compact={compact}
            ptzCctvId={item.cctv.ptz ? item.cctv.id : null}
            onFocus={() => onFocusStream(item.streamName)}
          />
        </Widget>
      ))}
      {Array.from({ length: emptyCount }, (_, i) => (
        <Widget
          key={`empty-${i}`}
          id={`empty-${i}`}
          border={false}
          className="h-full rounded-lg bg-transparent shadow-none"
          contentClassName="h-full p-0"
        >
          <EmptySlot />
        </Widget>
      ))}
    </GridLayout>
  );
}

function PageArrow({
  side,
  onClick,
  label,
}: {
  side: "left" | "right";
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`absolute top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/60 text-white shadow-lg backdrop-blur-sm transition hover:bg-black/80 ${
        side === "left" ? "-left-3" : "-right-3"
      }`}
    >
      {side === "left" ? <ChevronLeft size="md" /> : <ChevronRight size="md" />}
    </button>
  );
}

function PageDots({
  total,
  current,
  onSelect,
}: {
  total: number;
  current: number;
  onSelect: (page: number) => void;
}) {
  return (
    <div className="absolute -bottom-5 left-1/2 z-30 flex -translate-x-1/2 items-center">
      {Array.from({ length: total }, (_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(i)}
          aria-label={`${i + 1}번째 페이지`}
          aria-current={i === current ? "true" : undefined}
          className="group p-1"
        >
          <span
            className={`block h-1.5 w-1.5 rounded-full transition-all ${
              i === current
                ? "w-3 bg-brand"
                : "bg-neutral-300 group-hover:bg-neutral-400 dark:bg-white/25 dark:group-hover:bg-white/40"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function SpotlightGrid({
  items,
  page,
  onFocusStream,
}: {
  items: CCTVStreamItem[];
  page: number;
  onFocusStream: (streamName: string) => void;
}) {
  const perPage = SPOTLIGHT_TEMPLATE.cells.length;
  const pageItems = items.slice(page * perPage, (page + 1) * perPage);
  const emptyCount = Math.max(0, perPage - pageItems.length);

  const firstId = pageItems[0]?.streamName ?? "";
  const [spotlightId, setSpotlightId] = useState(firstId);
  const [prevFirstId, setPrevFirstId] = useState(firstId);
  if (firstId !== prevFirstId) {
    setPrevFirstId(firstId);
    setSpotlightId(firstId);
  }

  const handleLayoutChange = (e: LayoutChangeEvent) => {
    if (e.type !== "swap") return;
    const big = e.layout.placements.find((p) => p.cellId === "big");
    if (big) setSpotlightId(big.widgetId);
  };

  return (
    <GridLayout
      template={SPOTLIGHT_TEMPLATE}
      gap={6}
      editable
      onLayoutChange={handleLayoutChange}
      className="h-full overflow-y-visible p-0"
    >
      {pageItems.map((item) => {
        const isBig = item.streamName === spotlightId;
        return (
          <Widget
            key={item.streamName}
            id={item.streamName}
            border={false}
            className="h-full rounded-lg bg-transparent shadow-none"
            contentClassName="h-full p-0"
          >
            <LiveStreamPlayer
              streamUrl={isBig ? item.whepUrl : (item.whepUrlLow ?? item.whepUrl)}
              streamUrlHigh={item.whepUrl}
              name={item.displayName}
              compact={!isBig}
              ptzCctvId={item.cctv.ptz ? item.cctv.id : null}
              onFocus={() => onFocusStream(item.streamName)}
            />
          </Widget>
        );
      })}
      {Array.from({ length: emptyCount }, (_, i) => (
        <Widget
          key={`empty-${i}`}
          id={`empty-${i}`}
          border={false}
          className="h-full rounded-lg bg-transparent shadow-none"
          contentClassName="h-full p-0"
        >
          <EmptySlot />
        </Widget>
      ))}
    </GridLayout>
  );
}

function PlaybackButton({ siteId }: { siteId: string | undefined }) {
  const navigate = useNavigate();
  const enabled = isPlaybackEnabledSite(siteId);

  return (
    <button
      type="button"
      onClick={enabled && siteId ? () => navigate(cctvPath(siteId, "playback")) : undefined}
      disabled={!enabled}
      aria-label="녹화영상 페이지로 이동"
      title={enabled ? "녹화영상 보기" : "이 현장은 녹화영상이 제공되지 않습니다"}
      className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
        enabled
          ? "border-brand/40 bg-brand/10 text-brand hover:bg-brand/20"
          : "cursor-not-allowed border-neutral-200 dark:border-[#2A2D3A] bg-white dark:bg-[#1A1D27] text-neutral-400 dark:text-white/20"
      }`}
    >
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      PLAY BACK
    </button>
  );
}

interface VideoPanelProps {
  siteId?: string;
  items: CCTVStreamItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  selectedStream: string;
  onStreamChange: (streamName: string) => void;
  focusStream?: string | null;
  selectedEvent: StompEventResponse | null;
  onClearEvent: () => void;
  isMobile?: boolean;
}

export function VideoPanel({
  siteId,
  items,
  isLoading,
  isError,
  error,
  selectedStream,
  onStreamChange,
  focusStream,
  selectedEvent,
  onClearEvent,
  isMobile,
}: VideoPanelProps) {
  const [userGridMode, setUserGridMode] = useState<GridMode | null>(null);
  const [page, setPage] = useState(0);

  const autoGridMode: GridMode =
    items.length <= 1 ? "1x1" : items.length <= 4 ? "2x2" : items.length <= 9 ? "3x3" : "4x4";
  const focusMode: GridMode | null =
    focusStream && items.some((i) => i.streamName === focusStream) ? "1x1" : null;
  const gridMode = userGridMode ?? focusMode ?? autoGridMode;

  const [lastSiteId, setLastSiteId] = useState(siteId);
  if (siteId !== lastSiteId) {
    setLastSiteId(siteId);
    setUserGridMode(null);
    setPage(0);
  }

  const effectiveGridMode = isMobile ? "1x1" : gridMode;

  /**
   * 그리드에서 배지를 눌렀을 때 — 그 카메라를 골라 1×1 로 넘긴다.
   *
   * 예전에는 그리드 모드를 1×1 로 바꾼 뒤 드롭다운에서 카메라를 다시 찾는 두
   * 단계였다. 되돌아가는 길은 기존 그리드 모드 선택기가 그대로 맡는다.
   */
  const focusStreamAsSingle = (streamName: string) => {
    onStreamChange(streamName);
    setUserGridMode("1x1");
    setPage(0);
  };

  const perPage =
    effectiveGridMode === "1x1"
      ? 1
      : effectiveGridMode === "2x2"
        ? 4
        : effectiveGridMode === "3x3"
          ? 9
          : effectiveGridMode === "spot"
            ? 8
            : 16;
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const safePage = Math.min(page, totalPages - 1);

  if (selectedEvent) {
    return (
      <div className="flex h-full flex-col gap-3">
        <div className="flex min-h-10 items-center justify-between">
          <span className="text-sm font-medium text-neutral-600 dark:text-white/60">
            이벤트 영상
          </span>
          <button
            onClick={onClearEvent}
            className="rounded-md border border-neutral-200 dark:border-[#2A2D3A] bg-neutral-100 dark:bg-[#252833] px-3 py-1.5 text-xs text-neutral-600 dark:text-white/60 transition-colors hover:text-neutral-900 dark:hover:text-white"
          >
            라이브로 돌아가기
          </button>
        </div>
        <div className="flex-1">
          <EventVideoPlayer
            event={selectedEvent}
            onClose={onClearEvent}
            landscapeOnFullscreen={isMobile}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 dark:border-white/30 border-t-neutral-900 dark:border-t-white" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-severity-danger-700">
        {error?.message || "CCTV 목록 로딩 실패"}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-neutral-500 dark:text-white/40">
        등록된 CCTV가 없습니다
      </div>
    );
  }

  const activeStream = selectedStream || items[0]!.streamName;
  const activeItem = items.find((i) => i.streamName === activeStream) ?? items[0]!;
  const isSingle = effectiveGridMode === "1x1";

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex min-h-10 items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="hidden shrink-0 text-sm font-medium text-neutral-600 dark:text-white/60 sm:inline">
            라이브 스트림
          </span>
          {isSingle && (
            <CCTVSelect items={items} selected={activeStream} onChange={onStreamChange} />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {!isMobile && (
            <GridModeSelector
              mode={gridMode}
              onChange={(m) => {
                setUserGridMode(m);
                setPage(0);
              }}
            />
          )}
          <PlaybackButton siteId={siteId} />
        </div>
      </div>

      <div className="relative flex-1 min-h-0">
        {!isSingle && totalPages > 1 && safePage > 0 && (
          <PageArrow side="left" label="이전 페이지" onClick={() => setPage(safePage - 1)} />
        )}
        {!isSingle && totalPages > 1 && safePage < totalPages - 1 && (
          <PageArrow side="right" label="다음 페이지" onClick={() => setPage(safePage + 1)} />
        )}
        {!isSingle && totalPages > 1 && (
          <PageDots total={totalPages} current={safePage} onSelect={setPage} />
        )}

        {isSingle ? (
          <div className="relative h-full w-full">
            <LiveStreamPlayer
              streamUrl={activeItem.whepUrl}
              name={activeItem.displayName}
              landscapeOnFullscreen={isMobile}
              ptzCctvId={activeItem.cctv.ptz ? activeItem.cctv.id : null}
            />
          </div>
        ) : effectiveGridMode === "spot" ? (
          <SpotlightGrid items={items} page={safePage} onFocusStream={focusStreamAsSingle} />
        ) : (
          <LiveGrid
            items={items}
            gridMode={effectiveGridMode as "2x2" | "3x3" | "4x4"}
            page={safePage}
            onFocusStream={focusStreamAsSingle}
          />
        )}
      </div>
    </div>
  );
}
